/**
 * Development Authentication Codes
 *
 * Ported from the experimental monorepo (apps/unified-app/src/config/devAuthCodes.js).
 * These codes let a developer instantly authenticate as a specific role on the
 * local Vite dev server WITHOUT SMS/email verification.
 *
 * A dev code maps to a synthetic `<role>@dev.local` Firebase Auth account. The
 * shared password is read only on that dev server; it is not stored in this
 * module. On sign-in the `users/{uid}` doc is stamped with
 * `isDevAccount: true` + `devAuthCode`, which `isDevAuthFirestoreUser` keys off of to
 * bypass subscription/tier gates and CareStart/CareCompris session caps.
 *
 * SECURITY: {@link isDevAuthEnvironment} returns false in every production build,
 * so public hosts — including hlthdsk-experimental* — cannot use dev codes.
 * The host allowlist below is unchanged (localhost, 192.168.*, and the
 * experimental hosts) and is only evaluated on the local dev server.
 */

import {
  CARE_MAP_ROLE_LABELS,
  CARE_MAP_ROLES,
} from "shared/utils/constants/careMapRoles";

/** Shared dev clinic/facility/department/location ids (affiliation testing). */
export const DEV_CLINIC_ID = "dev-clinic-001";
export const DEV_FACILITY_ID = "dev-clinic-001";
export const DEV_DEPARTMENT_ID = "dev-dept-001";
export const DEV_TEMPLATE_ID = "dev-template-001";
export const DEV_LOCATION_ID = "dev-location-001";

/**
 * Default mock institution for `@dev.local` / help-video accounts that have no
 * organizationId on their DEV_AUTH_CODES entry. Must be an org that already has
 * VisitStart (and related TCI) seed — do not invent an empty tenant.
 */
export const DEV_INSTITUTION_ID = "inst-a";
export const DEV_INSTITUTION_NAME = "Institution A Hospital";

/**
 * Institution id to attach when a mock/dev account is missing affiliation.
 * Prefers the code's configured organizationId; otherwise {@link DEV_INSTITUTION_ID}.
 * @param {object|null|undefined} devData
 * @param {string} [fallback]
 * @returns {string}
 */
export function resolveDevInstitutionId(devData, fallback = DEV_INSTITUTION_ID) {
  const fromCode = String(devData?.organizationId || "").trim();
  if (fromCode) return fromCode;
  return String(fallback || DEV_INSTITUTION_ID).trim() || DEV_INSTITUTION_ID;
}

/** Institution-scoping keys users cannot self-write (firestore.rules update lock). */
export const DEV_AFFILIATION_FIELD_KEYS = [
  "activeInstitutionId",
  "institutionId",
  "organizationId",
  "region",
  "hierarchyInstitutionId",
];

/**
 * Drop affiliation fields from a client merge of an existing user doc.
 * Adding them on update is permission-denied and aborts the rest of the stamp.
 */
export function omitDevAffiliationFields(fields) {
  if (!fields || typeof fields !== "object") return {};
  const next = { ...fields };
  DEV_AFFILIATION_FIELD_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/** Dev auth code for the mock patient with a populated CMR (check-in + ChartMind). */
export const DEV_MOCK_PATIENT_CODE = "100010";

/**
 * First code for CareMap viewer-role preview accounts (`careMapRole` on the user doc).
 * These accounts use users.role patient. They start at 150001.
 */
export const CARE_MAP_ROLE_DEV_CODE_START = 150001;

function buildCareMapRoleDevAuthEntries() {
  const entries = {};
  CARE_MAP_ROLES.forEach((careMapRole, index) => {
    const code = String(CARE_MAP_ROLE_DEV_CODE_START + index);
    const label = CARE_MAP_ROLE_LABELS[careMapRole];
    entries[code] = {
      role: "patient",
      careMapRole,
      name: label,
      email: `caremap-${careMapRole.replace(/_/g, "-")}@dev.local`,
      description: `CareMap role preview — ${label}`,
    };
  });
  return entries;
}

/**
 * True when dev auth codes are allowed (localhost + Firebase experimental hosts).
 * Intentionally excludes the deployed caremap2020 / production domains.
 */
export const isDevAuthEnvironment = () => {
  // Vite replaces process.env.NODE_ENV with the build mode. `vite build` makes
  // this return false and drops the host checks. Jest (NODE_ENV=test) and `vite`
  // (mode development) still evaluate the allowlist.
  if (process.env.NODE_ENV === "production") return false;
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.startsWith("192.168.") ||
    hostname === "hlthdsk-experimental.web.app" ||
    hostname === "hlthdsk-experimental.firebaseapp.com" ||
    hostname === "hlthdsk-experimental-demo.web.app" ||
    hostname === "hlthdsk-experimental-demo.firebaseapp.com" ||
    process.env.REACT_APP_ENVIRONMENT === "experimental"
  );
};

export const DEV_AUTH_CODES = {
  // Patient roles
  "100001": {
    role: "patient",
    name: "Test Patient",
    email: "patient@dev.local",
    description:
      "Basic patient account. isDevAccount bypasses client subscription gates and CareStart session caps.",
  },
  "100010": {
    role: "patient",
    name: "John Smith",
    email: "mock-patient@dev.local",
    description: "Mock patient with populated CMR for check-in and ChartMind",
    isMockPatient: true,
  },
  "100011": {
    role: "patient",
    name: "Symptom Checker Dev",
    email: "symptomchecker@dev.local",
    description: "Standalone symptom checker (dev)",
  },
  "100012": {
    role: "professional-chartmind",
    name: "ChartMind Standalone Dev",
    email: "chartmind-standalone@dev.local",
    description: "Standalone ChartMind (dev)",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
  },
  "100013": {
    role: "facility",
    name: "Get on the Map Dev",
    email: "getonthemap@dev.local",
    description: "Standalone Get on the map (dev)",
    facilityId: "dev-clinic-001",
  },
  "12345T": {
    role: "professional-chartmind",
    name: "CareCompris Dev",
    email: "carecompris-dev@dev.local",
    description: "Standalone CareCompris (dev)",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
  },
  "123452": {
    role: "professional-chartmind",
    name: "CareVoice2 Dev",
    email: "carevoice2-dev@dev.local",
    description: "Standalone CareVoice2 (dev)",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
  },
  "123453": {
    role: "professional-chartmind",
    name: "CareVoice3 Dev",
    email: "carevoice3-dev@dev.local",
    description: "Standalone CareVoice3 (dev)",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
  },
  "100003": {
    role: "p2",
    name: "Test P2",
    email: "p2@dev.local",
    description: "P2 account (same as patient)",
  },
  "100004": {
    role: "p4",
    name: "Test P4",
    email: "p4@dev.local",
    description: "P4 account (same as patient / p2)",
  },

  // Professional roles
  "200001": {
    role: "professional",
    name: "Test Professional",
    email: "professional@dev.local",
    description: "General healthcare professional",
  },

  // ChartMind specific roles
  "300003": {
    role: "professional-chartmind",
    name: "ChartMind Provider",
    email: "chartmind-provider@dev.local",
    description: "ChartMind professional - read-only queue access",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
  },
  "300008": {
    role: "professional-chartmind",
    name: "Orphaned Professional",
    email: "orphan-pro@dev.local",
    description: "Professional without a facility - for testing join request",
  },
  "300009": {
    role: "professional-chartmind",
    name: "Multi-Facility Pro",
    email: "multi-facility@dev.local",
    description: "Professional with multiple facility assignments",
    facilityId: "dev-clinic-001",
    departmentId: "dev-dept-001",
    facilities: [
      { facilityId: "dev-clinic-001", departmentId: "dev-dept-001", role: "professional-chartmind" },
      { facilityId: "dev-clinic-002", departmentId: "dev-dept-002", role: "chartmind-supervisor" },
    ],
  },

  // Facility roles
  "500001": {
    role: "facility",
    name: "Test Facility",
    email: "facility@dev.local",
    description: "Healthcare facility account",
    facilityId: "dev-clinic-001",
  },
  "500003": {
    role: "facility",
    name: "Test Urgent Care Facility",
    email: "urgent-care-facility@dev.local",
    description: "Urgent Care facility test account",
    facilityId: "dev-clinic-uc-001",
  },

  // MSI3 / AcuityDrill (dev environments only)
  "710001": {
    role: "professional",
    name: "MSI3 Student",
    email: "msi3-student@dev.local",
    description: "MSI3 student — free tier",
  },
  "710002": {
    role: "professional",
    name: "MSI3 Clinician",
    email: "msi3-clinician@dev.local",
    description: "MSI3 clinician — free tier",
  },
  "710003": {
    role: "professional",
    name: "MSI3 Faculty",
    email: "msi3-faculty@dev.local",
    description: "MSI3 faculty grant at the dev school org",
    organizationId: "msi3-dev-school",
  },
  "710004": {
    role: "admin",
    name: "MSI3 School Admin",
    email: "msi3-school-admin@dev.local",
    description: "MSI3 school org admin",
    organizationId: "msi3-dev-school",
  },
  "710005": {
    role: "admin",
    name: "MSI3 Global Admin",
    email: "msi3-global-admin@dev.local",
    description: "MSI3 global admin (admin claim set by seed script)",
  },
  "710006": {
    role: "professional",
    name: "MSI3 Student Paid",
    email: "msi3-student-paid@dev.local",
    description: "MSI3 student with a fake active student plan",
  },
  "710007": {
    role: "professional",
    name: "MSI3 Clinician Paid",
    email: "msi3-clinician-paid@dev.local",
    description: "MSI3 clinician with a fake active clinician plan",
  },
  "710008": {
    role: "professional",
    name: "MSI3 Student Seat",
    email: "msi3-student-seat@dev.local",
    description: "MSI3 student seated in the 710004 school org",
    organizationId: "msi3-dev-school",
  },

  "900002": {
    role: "btybMarketer",
    name: "BTYB Marketer",
    email: "btyb-marketer@dev.local",
    description:
      "BTYB Marketer — city CPM auction for the Caremap2 Brought to you by banner",
  },

  // TCI Manager live demo walkthrough (tests-e2e/demo/tciManagerWalkthrough.demo.spec.js)
  "120001": {
    role: "admin",
    name: "Hospital A Admin",
    email: "tci-demo-hospital-a@dev.local",
    description: "TCI demo — Hospital A institution admin / TCI Manager",
    organizationId: "demo-hospital-a",
  },
  "120002": {
    role: "admin",
    name: "Global TCI Manager",
    email: "tci-demo-gtm@dev.local",
    description: "TCI demo — Global TCI Manager (platform catalog reviewer)",
  },
  "120003": {
    role: "admin",
    name: "Hospital B Admin",
    email: "tci-demo-hospital-b@dev.local",
    description: "TCI demo — Hospital B institution admin (cross-institution visibility)",
    organizationId: "demo-hospital-b",
  },
  "120011": {
    role: "professional",
    name: "Pilot Clinical Annotator Triage",
    email: "tci-pilot-annotator-triage@dev.local",
    description: "TCI pilot — Clinical Annotator for triage",
    organizationId: "demo-hospital-a",
  },
  "120012": {
    role: "professional",
    name: "Pilot Clinical Annotator Differential",
    email: "tci-pilot-fdec-ddx@dev.local",
    description: "TCI pilot — Clinical Annotator for differential",
    organizationId: "demo-hospital-a",
  },
  "120013": {
    role: "professional",
    name: "Pilot Clinical Annotator MedRec",
    email: "tci-pilot-fdec-medrec@dev.local",
    description: "TCI pilot — Clinical Annotator for medrec-intake",
    organizationId: "demo-hospital-a",
  },
  "120014": {
    role: "admin",
    name: "Pilot Technical Readiness Lead",
    email: "tci-pilot-readiness-lead@dev.local",
    description: "TCI pilot — Technical Readiness Lead (institution-wide) for Riverside / demo-hospital-a",
    organizationId: "demo-hospital-a",
  },
  "120021": {
    role: "admin",
    name: "RiverFront Urgent Care Admin",
    email: "tci-demo-riverfront@dev.local",
    description: "RiverFront Urgent Care — institution admin / TCI happy-path help video",
    organizationId: "demo-riverfront-uc",
  },

  // TCI ecosystem Playwright verification (tests-e2e/tciEcosystemFull.spec.js)
  "130001": {
    role: "admin",
    name: "Ecosystem Global Admin",
    email: "eco-global-admin@dev.local",
    description: "TCI ecosystem — platform global admin",
  },
  "130002": {
    role: "admin",
    name: "Ecosystem Global TCI Manager",
    email: "eco-global-tci-mgr@dev.local",
    description: "TCI ecosystem — Global TCI Manager (sharing queue + catalog)",
  },
  "130003": {
    role: "admin",
    name: "Institution A Admin",
    email: "eco-admin-a@dev.local",
    description: "TCI ecosystem — inst-a institution admin",
    organizationId: "inst-a",
  },
  "130004": {
    role: "admin",
    name: "Institution A TCI Manager",
    email: "eco-tci-mgr-a@dev.local",
    description: "TCI ecosystem — inst-a TCI Manager",
    organizationId: "inst-a",
  },
  "130005": {
    role: "professional",
    name: "Institution A Clinical Annotator",
    email: "eco-fdec-a@dev.local",
    description: "TCI ecosystem — inst-a certified Clinical Annotator (triage)",
    organizationId: "inst-a",
  },
  "130006": {
    role: "professional",
    name: "Institution A Clinical Annotator (uncertified)",
    email: "eco-fdec-uncert-a@dev.local",
    description: "TCI ecosystem — inst-a pending-training Clinical Annotator (hpi)",
    organizationId: "inst-a",
  },
  "130007": {
    role: "admin",
    name: "Institution B Admin",
    email: "eco-admin-b@dev.local",
    description: "TCI ecosystem — inst-b institution admin",
    organizationId: "inst-b",
  },
  "130008": {
    role: "professional",
    name: "Institution B User",
    email: "eco-user-b@dev.local",
    description: "TCI ecosystem — inst-b professional (no staffing role)",
    organizationId: "inst-b",
  },
  "130009": {
    role: "admin",
    name: "Institution A TCI Manager (no domain)",
    email: "eco-tci-mgr-no-domain@dev.local",
    description: "TCI ecosystem — TCI Manager with no assigned clinical domain",
    organizationId: "inst-a",
  },
  "130010": {
    role: "professional",
    name: "Institution A TCI Manager (pure)",
    email: "eco-tci-mgr-pure-a@dev.local",
    description: "TCI ecosystem — pure TCI Manager (slim TciManagerDrawer nav)",
    organizationId: "inst-a",
  },
  "130011": {
    role: "admin",
    name: "Institution A TCI Manager (fresh onboarding)",
    email: "eco-tci-mgr-fresh-a@dev.local",
    description: "TCI ecosystem — TCI Manager with incomplete orientation",
    organizationId: "inst-a",
  },
  "130012": {
    role: "professional",
    name: "Clinical Annotator Journey Test",
    email: "fdec-journey-fdec@dev.local",
    description: "Clinical Annotator journey — certified annotator on triage (Playwright fdecJourney.spec.js)",
    organizationId: "inst-a",
  },
  "130013": {
    role: "admin",
    name: "Clinical Annotator Journey TCI Manager",
    email: "fdec-journey-tci-mgr@dev.local",
    description: "Clinical Annotator journey — TCI Manager for fdec-journey-domain",
    organizationId: "inst-a",
  },
  "130014": {
    role: "professional",
    name: "Clinical Annotator Journey Uncertified Temp",
    email: "fdec-journey-uncert-temp@dev.local",
    description: "Clinical Annotator journey — temporary pending-training user (FJ-07 only)",
    organizationId: "inst-a",
  },

  // Role gaps filled after dev-auth audit (130015+)
  "130015": {
    role: "regional_admin",
    name: "Regional Admin - East Africa",
    email: "regional-admin-eastafrica@dev.local",
    description: "Regional admin — East Africa prompt scope (users.region + scope.regions)",
    region: "east_africa",
    scope: { regions: ["east_africa"], localities: [] },
  },
  "130016": {
    role: "admin",
    name: "Institution A Technical Readiness Lead",
    email: "eco-fdet-a@dev.local",
    description: "TCI ecosystem — inst-a Technical Readiness Lead (prompt engineering; requires ecosystem seed)",
    organizationId: "inst-a",
  },
  "130017": {
    role: "dscr-admin",
    name: "DSCR Admin - Platform",
    email: "dscr-admin@dev.local",
    description: "DSCR registry admin — symptom checker factor / cant-miss registry",
  },
  "130018": {
    name: "VoxPont Product User",
    email: "voxpont-dev@dev.local",
    description: "VoxPont-only account (product: voxpont, no HealthDesk role)",
    product: "voxpont",
  },
  "130019": {
    role: "professional",
    name: "Ecosystem CEO Claimant",
    email: "eco-ceo-claimant@dev.local",
    description:
      "TCI ecosystem — fresh CEO for generateInstitutionInvite → claim → /setup/institution",
  },
  "130020": {
    role: "professional",
    name: "Institution A VisitStart Plain Member",
    email: "eco-vs-plain-a@dev.local",
    description:
      "VisitStart element-config e2e — same-institution member without TCI Manager / admin (negative save)",
    organizationId: "inst-a",
  },
  "130021": {
    role: "patient",
    name: "Institution A VisitStart Patient",
    email: "eco-vs-patient-a@dev.local",
    description:
      "VisitStart session-shell e2e — patient at inst-a (self-service + proxy target)",
    organizationId: "inst-a",
  },
  "130022": {
    role: "professional",
    name: "Institution A VisitStart Staff B",
    email: "eco-vs-staff-b@dev.local",
    description:
      "VisitStart staff-proxy CMR e2e — second professional (negative grant use)",
    organizationId: "inst-a",
  },
  "130023": {
    role: "patient",
    name: "Institution A VisitStart Part1 Patient",
    email: "eco-vs-p1@dev.local",
    description: "VisitStart Part 1 e2e — medication / TB / clarifyingQuestions",
    organizationId: "inst-a",
  },
  "130024": {
    role: "patient",
    name: "Institution A VisitStart Part2 Patient",
    email: "eco-vs-p2@dev.local",
    description: "VisitStart Part 2 e2e — identity / SHA",
    organizationId: "inst-a",
  },
  "130025": {
    role: "patient",
    name: "Institution A VisitStart Part3 Patient",
    email: "eco-vs-p3@dev.local",
    description: "VisitStart Part 3 e2e — payment / dependents",
    organizationId: "inst-a",
  },
  "130026": {
    role: "patient",
    name: "Institution A VisitStart Part4 Patient",
    email: "eco-vs-p4@dev.local",
    description: "VisitStart Part 4 e2e — photo ID",
    organizationId: "inst-a",
  },
  "130027": {
    role: "patient",
    name: "Institution A VisitStart Part5 Patient",
    email: "eco-vs-p5@dev.local",
    description: "VisitStart Part 5 e2e — offline queue",
    organizationId: "inst-a",
  },
  "130028": {
    role: "patient",
    name: "Institution A VisitStart Part7 Patient",
    email: "eco-vs-p7@dev.local",
    description: "VisitStart Part 7 e2e — ROS draft disclosure",
    organizationId: "inst-a",
  },
  "130029": {
    role: "patient",
    name: "Institution A VisitStart Part8 Patient",
    email: "eco-vs-p8@dev.local",
    description: "VisitStart Part 8 e2e — annotation dual-write bridge",
    organizationId: "inst-a",
  },
  "130030": {
    role: "patient",
    name: "Institution A VisitStart Part9 Patient",
    email: "eco-vs-p9@dev.local",
    description: "VisitStart Part 9 e2e — ChartMind clarifyingQuestions handoff",
    organizationId: "inst-a",
  },
  "130031": {
    role: "patient",
    name: "Institution A VisitStart Part9 No-VS Patient",
    email: "eco-vs-p9b@dev.local",
    description: "VisitStart Part 9 e2e — redeem with no VisitStart session",
    organizationId: "inst-a",
  },
  "130032": {
    role: "patient",
    name: "Institution A VisitStart Part10 Off Patient",
    email: "eco-vs-p10off@dev.local",
    description: "VisitStart Part 10 e2e — SavoirCare toggle off",
    organizationId: "inst-a",
  },
  "130033": {
    role: "patient",
    name: "Institution A VisitStart Part10 On Patient",
    email: "eco-vs-p10on@dev.local",
    description: "VisitStart Part 10 e2e — SavoirCare toggle on",
    organizationId: "inst-a",
  },
  "130034": {
    role: "patient",
    name: "Institution A VisitStart Share Patient",
    email: "eco-vs-share@dev.local",
    description:
      "VisitStart completion e2e — MasterChart share QR into ClinicalHUD2 /share?c=",
    organizationId: "inst-a",
  },
  "130040": {
    role: "patient",
    name: "Amara Wanjiku",
    email: "amara.wanjiku@dev.local",
    description:
      "VisitStart help-video demo — dedicated patient (not used by e2e 130020–130033)",
    organizationId: "inst-a",
  },
  "130041": {
    role: "professional",
    name: "James Mwangi",
    email: "james.mwangi@dev.local",
    description:
      "VisitStart help-video demo — dedicated professional / staff-proxy + ChartMind",
    organizationId: "inst-a",
  },
  "130050": {
    role: "patient",
    name: "Amina Otieno",
    email: "amina.otieno@dev.local",
    description:
      "VisitStart complete-tour help video — dedicated patient (not 130040 / e2e 130020–130033)",
    organizationId: "inst-a",
  },
  "130051": {
    role: "patient",
    name: "Daniel Kariuki",
    email: "daniel.kariuki@dev.local",
    description:
      "VisitStart complete-tour help video — ChartMind fail-open patient (no VisitStart session)",
    organizationId: "inst-a",
  },
  "130052": {
    role: "professional",
    name: "Grace Njeri",
    email: "grace.njeri@dev.local",
    description:
      "VisitStart complete-tour help video — dedicated professional / staff-proxy + ChartMind",
    organizationId: "inst-a",
  },
  "130070": {
    role: "patient",
    name: "Loop Demo Maya Chen",
    email: "eco-loop-patient@dev.local",
    description:
      "Information-loop help video — signed-in demo patient (not anonymous, not staff-proxy)",
    organizationId: "inst-a",
  },
  "130071": {
    role: "professional",
    name: "Loop Demo Elena Vasquez",
    email: "eco-loop-ed-physician@dev.local",
    description:
      "Information-loop help video — ED physician with clinicalHudAccess",
    organizationId: "inst-a",
    facilityId: "inst-a",
  },
  "130072": {
    role: "facility",
    name: "Loop Demo Jordan Hale",
    email: "eco-loop-facility-admin@dev.local",
    description:
      "Information-loop help video — facility admin (SavoirCare board)",
    organizationId: "inst-a",
    facilityId: "inst-a",
  },

  // ASI Applied Superintelligence mock/demo personas (scripts/seedAsiMockDemo.js)
  "140001": {
    role: "professional",
    name: "[MOCK ASI] Trainer",
    email: "asi-trainer@dev.local",
    description:
      "Mock/demo ASI Trainer — feedback + request candidate review (asiRoleAssignments)",
    organizationId: "demo-asi-org",
  },
  "140002": {
    role: "admin",
    name: "[MOCK ASI] Org Interaction Manager",
    email: "asi-im-org@dev.local",
    description:
      "Mock/demo ASI org-scoped Interaction Manager — clone owner / candidate approve",
    organizationId: "demo-asi-org",
  },
  "140003": {
    role: "professional",
    name: "[MOCK ASI] BU Interaction Manager",
    email: "asi-im-bu@dev.local",
    description:
      "Mock/demo ASI business-unit-scoped Interaction Manager",
    organizationId: "demo-asi-org",
  },
  "140004": {
    role: "professional",
    name: "[MOCK ASI] Interaction-scoped IM",
    email: "asi-im-ix@dev.local",
    description:
      "Mock/demo ASI single-interaction-scoped Interaction Manager",
    organizationId: "demo-asi-org",
  },
  "140005": {
    role: "professional",
    name: "[MOCK ASI] End User",
    email: "asi-enduser@dev.local",
    description:
      "Mock/demo ASI end-user — occurrence run via /asi/run (no ASI role assignment)",
    organizationId: "demo-asi-org",
  },
  "140006": {
    role: "professional",
    name: "[MOCK ASI] Platform Staff",
    email: "asi-staff@dev.local",
    description:
      "Mock/demo ASI Staff principal (asiStaffAssignments) — view_suggest eligibility only; not GlobalManager",
  },

  ...buildCareMapRoleDevAuthEntries(),
};

/**
 * Check if a code is a valid dev auth code (only in a dev auth environment).
 * @param {string} code
 * @returns {boolean}
 */
export const isDevAuthCode = (code) => {
  if (!isDevAuthEnvironment()) return false;
  return Object.prototype.hasOwnProperty.call(DEV_AUTH_CODES, code);
};

/**
 * Get the role/email/affiliation data for a dev auth code, or null if invalid.
 * @param {string} code
 * @returns {object|null}
 */
export const getDevAuthData = (code) => {
  if (!isDevAuthCode(code)) return null;
  return DEV_AUTH_CODES[code];
};

/**
 * Affiliation and profile fields written to `users/{uid}` on dev login.
 * @param {object|null} devData - DEV_AUTH_CODES entry from getDevAuthData
 * @returns {object}
 */
export const buildDevUserAffiliationFields = (devData) => {
  if (!devData) return {};
  const fields = {};
  if (devData.facilityId) {
    fields.facilityId = devData.facilityId;
    fields.clinicId = devData.facilityId;
  }
  if (devData.clinicId) {
    fields.clinicId = devData.clinicId;
  }
  if (devData.departmentId) {
    fields.departmentId = devData.departmentId;
  }
  if (devData.location) {
    fields.location = devData.location;
  }
  if (devData.facilities) {
    fields.facilities = devData.facilities;
  }
  if (devData.organizationId) {
    fields.organizationId = devData.organizationId;
    fields.hierarchyInstitutionId = devData.organizationId;
    fields.activeInstitutionId = devData.organizationId;
  }
  if (devData.region) {
    fields.region = devData.region;
  }
  if (devData.scope) {
    fields.scope = devData.scope;
  }
  if (devData.level) {
    fields.level = devData.level;
  }
  if (devData.allowedViewingRoles) {
    fields.allowedViewingRoles = devData.allowedViewingRoles;
  }
  if (devData.product) {
    fields.product = devData.product;
  }
  return fields;
};

/**
 * Firestore user doc patch applied on dev sign-in (merge).
 * Omits `role` when the code entry has no HealthDesk role (e.g. VoxPont-only).
 * @param {object|null} devData
 * @returns {object}
 */
export const buildDevUserDocFields = (devData) => {
  if (!devData) return {};
  const fields = {
    name: devData.name,
    isDevAccount: true,
    ...buildDevUserAffiliationFields(devData),
  };
  if (devData.role != null && devData.role !== "") {
    fields.role = devData.role;
  }
  if (devData.careMapRole) {
    fields.careMapRole = devData.careMapRole;
  }
  return fields;
};

/**
 * Facility dashboards (Status Board, SavoirCare, etc.) resolve the facility via
 * `locations` docs whose `users` array contains the Auth uid — not via
 * `users.facilityId` alone. Dev auth stamps facilityId but historically never
 * created that membership doc, so Status Board spun forever on `!data.id`.
 *
 * Idempotently create/merge `locations/{facilityId}` and array-union the uid.
 *
 * @param {FirebaseFirestore.Firestore} db
 * @param {typeof import("firebase/compat/app").default} firebase
 * @param {{ uid: string, facilityId: string, title?: string }} opts
 */
export const ensureDevFacilityLocation = async (
  db,
  firebase,
  { uid, facilityId, title },
) => {
  const id = String(facilityId || "").trim();
  if (!uid || !id) return null;

  const ref = db.collection("locations").doc(id);
  const snap = await ref.get();

  if (!snap.exists) {
    await ref.set({
      id,
      title: title || "Dev Facility",
      type: "Clinic",
      status: "approved",
      users: [uid],
      waitTimes: [],
      diagnosticCapabilities: [],
      capabilities: {
        lab: false,
        xray: false,
        ultrasound: false,
        ct: false,
        mri: false,
      },
    });
  } else {
    await ref.set(
      {
        users: firebase.firestore.FieldValue.arrayUnion(uid),
        ...(snap.data()?.title ? {} : { title: title || "Dev Facility" }),
      },
      { merge: true },
    );
  }

  return id;
};

/**
 * All available dev codes (for a dev-only picker/console). Empty off dev environments.
 * @returns {Array<object>}
 */
export const getAllDevCodes = () => {
  if (!isDevAuthEnvironment()) return [];
  return Object.entries(DEV_AUTH_CODES).map(([code, data]) => ({ code, ...data }));
};

export default DEV_AUTH_CODES;
